'use client';

import { useCallback, useEffect, useState, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
import ConnectorBanner from '@/components/connector-banner';
import ConnectorManagementPage from '@/components/connector-management-page';
import ChooseConnectionTypeModal from '@/components/choose-connection-type-modal';
import AddMcpModal from '@/components/mcp/add-mcp-modal';
import AddConnectorModal from '@/components/add-connector-modal';
import ConnectorCreatedModal, { type CreatedConnector } from '@/components/connector-created-modal';
import PageLoadingState from '@/components/page-loading-state';
import AccessDeniedModal from '@/components/access-denied-modal';
import { domId } from '@/lib/dom-id';
import {
  type ConnectorFlowState,
  getFlowStateConnectorName,
  resumeConnectorFlow,
} from '@/lib/connector-flow-state';
import {
  GUIDANCE_STEP_ORDER,
  countCompletedGuidanceSteps,
  findUnfinishedConnector,
} from '@/lib/onboarding-guidance';
import type { SystemRecord } from '@/services/schemas/system-schemas';
import { useOnboardingProgress } from '@/services/hooks/use-onboarding';
import { useSystems } from '@/services/hooks/use-systems';
import { useConnectorAccess } from '@/hooks/use-connector-access';
import { useConnectorFlowSnapshot } from '@/hooks/use-connector-flow-snapshot';
import {
  CONNECTOR_ADD_API_SCREEN,
  CONNECTOR_AUTH_SCREEN,
  CONNECTOR_HOME_SCREEN,
  CONNECTOR_HOME_GUIDANCE_SCREEN,
  CONNECTOR_NEW_SCREEN,
} from '@/lib/connector-access';
import {
  buildWorkspaceAwarePath,
  getResolvedWorkspaceId,
  useActiveWorkspaceScopeKey,
} from '@/lib/active-workspace';
import {
  clearCompletionScreenDisplayState,
  getArmedCompletionScreenPayload,
} from '@/lib/completion-screen-state';
import { pushWithMinimumDelay } from '@/lib/navigation';
import { useConnectorHomeSidebarStore } from '@/store/connector-home/connector-home-sidebar-store';
import type { GuidanceStepId, GuidanceStepStatus } from '@/types/guidance';
import type { OnboardingProgress, OnboardingStep } from '@/types/onboarding';
import { Button } from '@/components/ui';
import { coerceLocale } from '@/i18n/locales';
import { useMcps } from '@/services/hooks/use-mcps';

type GuidanceImageAsset = {
  src: string;
  alt: string;
  width: number;
  height: number;
};

type HomeGuidanceStepId = 'add_connector' | 'configure_auth';

type GuidanceStep = {
  id: HomeGuidanceStepId;
  title: string;
  description: string;
  currentActionLabel: string;
  currentIcon: GuidanceImageAsset;
  pendingIcon?: GuidanceImageAsset;
  currentActionIcon: GuidanceImageAsset;
  pendingActionIcon?: GuidanceImageAsset;
};

type GettingStartedSectionProps = {
  onboardingProgress?: OnboardingProgress | null;
  flowState: ConnectorFlowState;
  // Set only when this browser holds no draft (see findUnfinishedConnector).
  unfinishedConnector: SystemRecord | null;
  hasLocalMcpIntegration: boolean;
  onOpenCreateModal: () => void;
  onOpenConnectorModalStep: (step: 'profile' | 'add-api' | 'review' | 'authentication') => void;
};

function getDraftConnectorName(flowState: ConnectorFlowState): string {
  return getFlowStateConnectorName(flowState);
}

function hasLocalApiDraft(flowState: ConnectorFlowState): boolean {
  return Boolean(
    flowState.previewActions.length > 0 || (flowState.hasAuthDraft && flowState.systemId)
  );
}

function createEffectiveGuidanceSteps(
  steps: OnboardingStep[] | null | undefined,
  localDraftState: {
    hasLocalConnectorDraft: boolean;
    hasLocalApiDraft: boolean;
    hasLocalAuthSetup: boolean;
  }
): OnboardingStep[] {
  const stepsById = new Map(steps?.map(step => [step.step_id, step]) ?? []);

  return GUIDANCE_STEP_ORDER.map(stepId => {
    const currentStep = stepsById.get(stepId);

    const localCompletionOverrides: Partial<Record<GuidanceStepId, boolean>> = {
      add_connector: localDraftState.hasLocalConnectorDraft,
      add_api: localDraftState.hasLocalApiDraft,
      configure_auth: localDraftState.hasLocalAuthSetup,
    };

    return {
      step_id: stepId,
      is_completed: Boolean(localCompletionOverrides[stepId]) || Boolean(currentStep?.is_completed),
      completed_at: currentStep?.completed_at,
    };
  });
}

const COMPLETED_STEP_ICON: GuidanceImageAsset = {
  src: '/onboarding/step-completed-icon.svg',
  alt: 'Completed',
  width: 16.3,
  height: 12.025,
};

const COMPLETED_BADGE_ICON: GuidanceImageAsset = {
  src: '/onboarding/badge-completed-check.svg',
  alt: 'Completed',
  width: 10,
  height: 10,
};

const REVIEW_ACTION_ICON: GuidanceImageAsset = {
  src: '/onboarding/action-review.svg',
  alt: 'Review',
  width: 16.5,
  height: 11.25,
};

function GettingStartedSection({
  onboardingProgress = null,
  flowState,
  unfinishedConnector,
  hasLocalMcpIntegration,
  onOpenCreateModal,
  onOpenConnectorModalStep,
}: GettingStartedSectionProps) {
  const purposeBase = 'home-onboarding';
  const router = useRouter();
  const locale = useLocale();
  const { canAccessScreen } = useConnectorAccess();
  const t = useTranslations('GettingStarted');
  const getMessage = (key: string, fallback: string, values?: Record<string, string | number>) => {
    try {
      return values ? t(key as never, values as never) : t(key as never);
    } catch {
      return fallback;
    }
  };

  const stepConfigs: GuidanceStep[] = useMemo(
    () => [
      {
        id: 'add_connector',
        title: t('steps.addConnector.title'),
        description: t('steps.addConnector.description'),
        currentActionLabel: t('actions.addConnector'),
        currentIcon: {
          src: '/onboarding/step-connector-current.svg',
          alt: t('steps.addConnector.alt'),
          width: 22,
          height: 22,
        },
        currentActionIcon: {
          src: '/onboarding/step-connector-current-cta.svg',
          alt: t('actions.addConnector'),
          width: 10.5,
          height: 10.5,
        },
      },
      {
        id: 'configure_auth',
        title: t('steps.addAuth.title'),
        description: t('steps.addAuth.description'),
        currentActionLabel: t('actions.configureAuth'),
        currentIcon: {
          src: '/onboarding/step-auth-current.svg',
          alt: t('steps.addAuth.alt'),
          width: 16,
          height: 20,
        },
        currentActionIcon: {
          src: '/onboarding/step-auth-current-cta.svg',
          alt: t('actions.configureAuth'),
          width: 17.25,
          height: 9,
        },
        pendingActionIcon: {
          src: '/onboarding/step-auth-pending.svg',
          alt: t('steps.addAuth.alt'),
          width: 16,
          height: 20,
        },
        pendingIcon: {
          src: '/onboarding/step-auth-pending.svg',
          alt: t('steps.addAuth.alt'),
          width: 16,
          height: 20,
        },
      },
    ],
    [t]
  );

  const connectorName = getDraftConnectorName(flowState);
  const displayedConnectorName = connectorName || unfinishedConnector?.name?.trim() || '';
  const localGuidanceState = useMemo(
    () => ({
      hasLocalConnectorDraft: Boolean(connectorName) || hasLocalMcpIntegration,
      hasLocalApiDraft: hasLocalApiDraft(flowState) || hasLocalMcpIntegration,
      hasLocalAuthSetup:
        Boolean(flowState.hasAuthDraft && flowState.systemId) || hasLocalMcpIntegration,
    }),
    [connectorName, flowState, hasLocalMcpIntegration]
  );
  const effectiveGuidanceSteps = useMemo(
    () => createEffectiveGuidanceSteps(onboardingProgress?.steps, localGuidanceState),
    [localGuidanceState, onboardingProgress?.steps]
  );
  const stepCompletionMap = useMemo(() => {
    const completed = new Set(
      effectiveGuidanceSteps.filter(step => step.is_completed).map(step => step.step_id)
    );

    return {
      addConnector: completed.has('add_connector'),
      addApi: completed.has('add_api'),
      configureAuth: completed.has('configure_auth'),
    };
  }, [effectiveGuidanceSteps]);
  const stepStatuses = useMemo<Record<HomeGuidanceStepId, GuidanceStepStatus>>(
    () => ({
      add_connector: stepCompletionMap.addConnector ? 'completed' : 'current',
      configure_auth: stepCompletionMap.configureAuth
        ? 'completed'
        : stepCompletionMap.addConnector
          ? 'current'
          : 'pending',
    }),
    [stepCompletionMap]
  );
  const completedCount = useMemo(
    () => [stepCompletionMap.addConnector, stepCompletionMap.configureAuth].filter(Boolean).length,
    [stepCompletionMap]
  );
  const stepOneCompletedDetail = getMessage(
    'states.step1CompletedDetail',
    'Profile created successfully. Ready for API configuration.'
  );
  const stepTwoCompletedDetail = getMessage(
    'states.step2CompletedDetail',
    'Authentication configured successfully.'
  );

  const resolveStepTargetScreen = useCallback(
    (stepId: HomeGuidanceStepId, stepStatus: GuidanceStepStatus) => {
      if (stepId === 'add_connector') {
        return CONNECTOR_NEW_SCREEN;
      }

      if (!flowState.systemId && !unfinishedConnector) {
        return CONNECTOR_NEW_SCREEN;
      }

      if (stepStatus === 'completed') {
        return CONNECTOR_AUTH_SCREEN;
      }

      return stepCompletionMap.addApi ? CONNECTOR_AUTH_SCREEN : CONNECTOR_ADD_API_SCREEN;
    },
    [flowState.systemId, stepCompletionMap.addApi, unfinishedConnector]
  );

  const buildStepHref = (stepId: HomeGuidanceStepId, stepStatus: GuidanceStepStatus): string => {
    const params = new URLSearchParams();
    if (connectorName) params.set('name', connectorName);
    if (flowState.systemId) params.set('system_id', flowState.systemId);

    if (stepId === 'add_connector') {
      return buildWorkspaceAwarePath(`/${locale}/connectors/new`, params);
    }

    if (!flowState.systemId) {
      return buildWorkspaceAwarePath(`/${locale}/connectors/new`);
    }

    if (stepStatus !== 'completed' && !stepCompletionMap.addApi) {
      return buildWorkspaceAwarePath(`/${locale}/connectors/add-api`, params);
    }

    return buildWorkspaceAwarePath(`/${locale}/connectors/authentication`, params);
  };

  const handleOpenStep = (step: GuidanceStep, stepStatus: GuidanceStepStatus) => {
    if (stepStatus === 'pending') return;
    if (step.id === 'add_connector' && stepStatus !== 'completed') {
      onOpenCreateModal();
      return;
    }
    if (unfinishedConnector) {
      // Continue the connector the server counts as step 1. The modal resolves
      // the rebuilt draft to Add API or Authentication, as for a browser draft.
      resumeConnectorFlow(unfinishedConnector);
      onOpenConnectorModalStep('profile');
      return;
    }
    if (step.id === 'add_connector' && stepStatus === 'completed') {
      onOpenConnectorModalStep('profile');
      return;
    }
    if (step.id === 'configure_auth') {
      // The review and authentication steps work on the draft's system; with no
      // draft in this browser, start from the profile, as buildStepHref does.
      onOpenConnectorModalStep(
        !flowState.systemId
          ? 'profile'
          : stepStatus === 'completed'
            ? 'authentication'
            : stepCompletionMap.addApi
              ? 'authentication'
              : 'review'
      );
      return;
    }

    void pushWithMinimumDelay(router, buildStepHref(step.id, stepStatus), {
      skipUnsavedCheck: true,
    });
  };

  if (!canAccessScreen(CONNECTOR_HOME_GUIDANCE_SCREEN)) {
    return null;
  }

  const totalSteps = stepConfigs.length;
  const progressPercentage = (completedCount / totalSteps) * 100;
  const visibleProgressWidth =
    completedCount === 0 ? Math.max(progressPercentage, 5) : progressPercentage;
  const progressFillStyle =
    completedCount === 0
      ? {
          background: 'linear-gradient(90deg, #713D98 0%, #3E3D98 140.38%)',
        }
      : { backgroundColor: '#2FA304' };

  return (
    <section data-testid={domId('div', `${purposeBase}-container`)} className="bg-white">
      <div data-testid={domId('div', `${purposeBase}-header`)}>
        <h3 className="text-[24px] font-medium leading-[1.4] text-[#0B0809]">{t('title')}</h3>
        <p className="mt-1 text-[14px] leading-[1.4] text-[#444746]">{t('description')}</p>
      </div>

      <div className="mt-6 rounded-2xl border border-[#E5E7EB] bg-white px-[25px] py-[25px] shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
        <div className="mb-2 flex items-end justify-between">
          <span className="text-[14px] font-semibold uppercase leading-[20px] tracking-[0.7px] text-[#0B0809]">
            {t('overallProgress')}
          </span>
          <span className="bg-gradient-to-r from-[#713D98] to-[#3E3D98] bg-clip-text text-[14px] font-medium leading-[20px] text-transparent">
            {t('progressSummary', { completedCount, totalSteps })}
          </span>
        </div>
        <div className="h-[10px] overflow-hidden rounded-full bg-[#EEEEFB]">
          <div
            data-testid={domId('div', `${purposeBase}-overall-progress-value`)}
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${visibleProgressWidth}%`,
              ...progressFillStyle,
            }}
          />
        </div>
      </div>

      <div data-testid={domId('div', `${purposeBase}-steps-flow`)} className="mt-6 space-y-4">
        {stepConfigs.map((stepConfig, index) => {
          const stepStatus = stepStatuses[stepConfig.id];
          const isCompleted = stepStatus === 'completed';
          const isCurrent = stepStatus === 'current';
          const isPending = stepStatus === 'pending';
          const statusLabel = isCompleted
            ? t('badges.completed')
            : isCurrent
              ? t('badges.current')
              : t('badges.pending');
          const stepDescription =
            isCompleted && stepConfig.id === 'add_connector'
              ? stepOneCompletedDetail
              : isCompleted && stepConfig.id === 'configure_auth'
                ? stepTwoCompletedDetail
                : stepConfig.description;
          const showConnectorMeta =
            isCompleted && stepConfig.id === 'add_connector' && displayedConnectorName;
          const stepIcon = isCompleted
            ? COMPLETED_STEP_ICON
            : isCurrent
              ? stepConfig.currentIcon
              : (stepConfig.pendingIcon ?? stepConfig.currentIcon);
          const actionIcon = isCompleted
            ? REVIEW_ACTION_ICON
            : isCurrent
              ? stepConfig.currentActionIcon
              : (stepConfig.pendingActionIcon ?? stepConfig.currentActionIcon);
          const actionLabel = isCompleted ? t('actions.review') : stepConfig.currentActionLabel;
          const statusPillClass = isCompleted
            ? 'bg-[#CDE9C3] text-[#288B03]'
            : 'bg-[#EBF5FF] text-[#025ECA]';
          const completedActionButtonClass =
            stepConfig.id === 'configure_auth'
              ? 'border border-[#D1D5DB] bg-white text-[#0B0809] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]'
              : 'border border-[#D1D5DB] bg-white text-[#374151] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]';
          const actionButtonClass = isCompleted
            ? completedActionButtonClass
            : isCurrent
              ? 'bg-gradient-to-r from-[#713D98] to-[#3E3D98] to-[140.38%] text-white shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]'
              : 'cursor-not-allowed border border-[#E5E7EB] bg-[#F3F4F6] text-[#8E9BA8]';
          const actionSizingClass = isCurrent
            ? 'h-10 px-[20px] py-[10px]'
            : 'h-[38px] px-[17px] py-[9px]';
          const canOpenStep = !isPending
            ? canAccessScreen(resolveStepTargetScreen(stepConfig.id, stepStatus))
            : false;
          const isActionDisabled = isPending || !canOpenStep;
          const resolvedActionButtonClass = isActionDisabled
            ? 'cursor-not-allowed border border-[#E5E7EB] bg-[#F3F4F6] text-[#8E9BA8]'
            : actionButtonClass;
          const resolvedActionIcon =
            isActionDisabled && !isCompleted
              ? (stepConfig.pendingActionIcon ?? stepConfig.currentActionIcon)
              : actionIcon;
          const resolvedActionIconClass =
            isActionDisabled && isCompleted ? 'opacity-50' : undefined;
          const cardHeightClass = isCompleted
            ? stepConfig.id === 'add_connector'
              ? 'min-h-[205px]'
              : 'min-h-[173px]'
            : isCurrent
              ? stepConfig.id === 'add_connector'
                ? 'min-h-[175px]'
                : 'min-h-[183px]'
              : 'min-h-[173px]';
          const contentGapClass =
            isCompleted || stepConfig.id === 'add_connector' ? 'gap-3' : 'gap-2';
          const descriptionClass =
            isCompleted && showConnectorMeta
              ? 'text-[14px] font-normal leading-[1.4] text-[#6B7280]'
              : 'text-[14px] font-normal leading-[1.4] text-[#444746]';
          const descriptionSpacingClass =
            isCurrent && stepConfig.id !== 'add_connector' ? 'pb-2' : '';

          return (
            <div
              key={stepConfig.id}
              data-testid={domId('div', `${purposeBase}-step-${stepConfig.id}-wrapper`)}
              data-card-surface={isCompleted || isCurrent ? 'true' : undefined}
              className={`relative overflow-hidden rounded-[16px] border bg-white p-8 transition-[background-color,border-color,box-shadow,transform] duration-200 ease-in-out ${cardHeightClass} ${
                isCompleted
                  ? 'border-[#F0F9EC] shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1)]'
                  : isCurrent
                    ? `${
                        stepConfig.id === 'add_connector'
                          ? 'border-[#EEEEFB]'
                          : 'border-[rgba(107,76,154,0.3)]'
                      } shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1)]`
                    : 'border-[#E5E7EB] bg-[#F9FAFB] opacity-80'
              }`}
            >
              {(isCompleted || isCurrent) && (
                <div
                  className={`absolute bottom-0 left-0 top-0 w-[6px] ${
                    isCompleted
                      ? 'bg-[#54B431]'
                      : 'bg-gradient-to-r from-[#713D98] to-[#3E3D98] to-[140.38%]'
                  }`}
                />
              )}
              <div className="flex items-start gap-6">
                <div className="flex flex-col">
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${
                      isCompleted
                        ? 'bg-[#CDE9C3]'
                        : isCurrent
                          ? 'bg-gradient-to-r from-[#713D98] to-[#3E3D98] to-[140.38%] shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]'
                          : 'border border-[#E5E7EB] bg-white'
                    }`}
                  >
                    <Image
                      src={stepIcon.src}
                      alt={stepIcon.alt}
                      width={stepIcon.width}
                      height={stepIcon.height}
                    />
                  </div>
                </div>
                <div className={`flex min-w-0 flex-1 flex-col ${contentGapClass}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <h4 className="text-[18px] font-semibold leading-[1.4] text-[#0B0809]">
                      {`${t('stepLabel', { index: index + 1 })}: ${stepConfig.title}`}
                    </h4>
                    {isPending ? (
                      <span
                        data-testid={domId('txt', `${purposeBase}-step-${stepConfig.id}-status`)}
                        className="sr-only"
                      >
                        {statusLabel}
                      </span>
                    ) : (
                      <span
                        data-testid={domId('txt', `${purposeBase}-step-${stepConfig.id}-status`)}
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0 text-xs font-normal leading-[1.4] ${statusPillClass}`}
                      >
                        {isCompleted && (
                          <Image
                            src={COMPLETED_BADGE_ICON.src}
                            alt={COMPLETED_BADGE_ICON.alt}
                            width={COMPLETED_BADGE_ICON.width}
                            height={COMPLETED_BADGE_ICON.height}
                          />
                        )}
                        {statusLabel}
                      </span>
                    )}
                  </div>
                  {showConnectorMeta && (
                    <p className="text-[14px] font-medium leading-[1.4] text-[#1F2937]">
                      {getMessage('states.connectorLabel', `Connector: ${displayedConnectorName}`, {
                        name: displayedConnectorName,
                      })}
                    </p>
                  )}
                  <p className={`${descriptionClass} ${descriptionSpacingClass}`}>
                    {stepDescription}
                  </p>
                  <Button
                    data-testid={domId('btn', `${purposeBase}-step-${stepConfig.id}-action`)}
                    data-primary-action={isCurrent && !isActionDisabled ? 'true' : undefined}
                    type="button"
                    onClick={() => handleOpenStep(stepConfig, stepStatus)}
                    disabled={isActionDisabled}
                    variant="unstyled"
                    className={`inline-flex w-fit items-center gap-2 rounded-[8px] text-[14px] font-medium leading-[1.4] ${actionSizingClass} ${resolvedActionButtonClass}`}
                  >
                    <Image
                      src={resolvedActionIcon.src}
                      alt={resolvedActionIcon.alt}
                      width={resolvedActionIcon.width}
                      height={resolvedActionIcon.height}
                      className={resolvedActionIconClass}
                    />
                    {actionLabel}
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function SystemManagementPage() {
  const router = useRouter();
  const locale = coerceLocale(useLocale());
  const workspaceScope = useActiveWorkspaceScopeKey();
  const flowState = useConnectorFlowSnapshot();
  const [createdConnector, setCreatedConnector] = useState<CreatedConnector | null>(null);

  const localDraftConnectorName = getDraftConnectorName(flowState);
  const {
    canCreateSystem,
    canViewSystems,
    canViewMcps,
    canCreateMcp,
    canAccessScreen,
    isLoading: isConnectorAccessLoading,
  } = useConnectorAccess();
  const canViewConnectorHome =
    (canViewSystems || canViewMcps) && canAccessScreen(CONNECTOR_HOME_SCREEN);
  const showGettingStarted = canCreateSystem && canAccessScreen(CONNECTOR_HOME_GUIDANCE_SCREEN);
  const showAddConnectorToolbar =
    (canCreateSystem && canAccessScreen(CONNECTOR_NEW_SCREEN)) || canCreateMcp;
  const {
    onboardingProgress,
    isLoading: isOnboardingProgressLoading,
    isValidating: isOnboardingProgressValidating,
  } = useOnboardingProgress(showGettingStarted);
  const setHomeSidebarSystems = useConnectorHomeSidebarStore(state => state.setSystems);
  const clearHomeSidebarSystems = useConnectorHomeSidebarStore(state => state.clearSystems);
  const {
    systems: allSystems,
    isLoading: isSystemsLoading,
    isValidating: isSystemsValidating,
  } = useSystems({
    enabled: canViewConnectorHome && !isConnectorAccessLoading,
    sortField: 'last_updated',
    sortOrder: 'desc',
    limit: 1000,
    offset: 0,
  });
  const {
    mcps: recentMcps,
    isLoading: isMcpsLoading,
    isValidating: isMcpsValidating,
  } = useMcps({
    enabled: canViewMcps && !isConnectorAccessLoading,
    limit: 1000,
    offset: 0,
    sort: { last_updated_time: -1 },
  });
  const cachedSystems = allSystems;
  // A draft in this browser wins; without one, Getting Started falls back to
  // the connector the server already counts as step 1.
  const unfinishedConnector = useMemo(
    () =>
      localDraftConnectorName || flowState.systemId ? null : findUnfinishedConnector(allSystems),
    [allSystems, flowState.systemId, localDraftConnectorName]
  );
  const isWorkspaceStatePending = workspaceScope === 'pending';
  const [resolvedWorkspaceScope, setResolvedWorkspaceScope] = useState<string | null>(null);
  const isWaitingForWorkspaceData =
    isWorkspaceStatePending ||
    isConnectorAccessLoading ||
    isSystemsLoading ||
    isSystemsValidating ||
    isMcpsLoading ||
    isMcpsValidating ||
    (showGettingStarted && (isOnboardingProgressLoading || isOnboardingProgressValidating));
  const isHomeStateResolved = resolvedWorkspaceScope === workspaceScope;
  const mcpServers = useMemo(() => recentMcps ?? [], [recentMcps]);
  const hasLocalMcpIntegration = mcpServers.length > 0;
  const localGuidanceState = useMemo(
    () => ({
      hasLocalConnectorDraft: Boolean(localDraftConnectorName) || hasLocalMcpIntegration,
      hasLocalApiDraft: hasLocalApiDraft(flowState) || hasLocalMcpIntegration,
      hasLocalAuthSetup:
        Boolean(flowState.hasAuthDraft && flowState.systemId) || hasLocalMcpIntegration,
    }),
    [flowState, hasLocalMcpIntegration, localDraftConnectorName]
  );
  const effectiveGuidanceSteps = useMemo(
    () => createEffectiveGuidanceSteps(onboardingProgress?.steps, localGuidanceState),
    [localGuidanceState, onboardingProgress?.steps]
  );
  const isOnboardingCompleted = useMemo(() => {
    if (!showGettingStarted) {
      return true;
    }
    return countCompletedGuidanceSteps(effectiveGuidanceSteps) === GUIDANCE_STEP_ORDER.length;
  }, [effectiveGuidanceSteps, showGettingStarted]);
  const showGuidance = isHomeStateResolved && showGettingStarted && !isOnboardingCompleted;
  const showSystemList = isHomeStateResolved && (!showGettingStarted || isOnboardingCompleted);
  useEffect(() => {
    setResolvedWorkspaceScope(null);
  }, [workspaceScope]);

  useEffect(() => {
    if (isWaitingForWorkspaceData) {
      return;
    }

    setResolvedWorkspaceScope(currentScope =>
      currentScope === workspaceScope ? currentScope : workspaceScope
    );
  }, [isWaitingForWorkspaceData, workspaceScope]);

  useEffect(() => {
    if (!showSystemList) {
      clearHomeSidebarSystems();
      return;
    }

    setHomeSidebarSystems(
      (cachedSystems ?? [])
        .map(system => ({
          systemId: system.system_id,
          name: system.name?.trim() || '',
          description: system.description?.trim() || '',
        }))
        .filter(system => Boolean(system.name))
    );

    return () => {
      clearHomeSidebarSystems();
    };
  }, [cachedSystems, clearHomeSidebarSystems, setHomeSidebarSystems, showSystemList]);

  useEffect(() => {
    if (!isHomeStateResolved) {
      return;
    }

    const resolvedWorkspaceId = getResolvedWorkspaceId() ?? '';
    const completionPayload = getArmedCompletionScreenPayload(resolvedWorkspaceId);
    if (!completionPayload) {
      return;
    }

    clearCompletionScreenDisplayState(
      resolvedWorkspaceId,
      completionPayload.systemId,
      completionPayload.connectorName
    );
    setCreatedConnector(completionPayload);
  }, [isHomeStateResolved]);

  // "Add Connector" now opens the type-selection modal (design 7952-138394,
  // AI-STORY-054 / PRD flow 5) instead of routing straight to /connectors/new.
  // MCP connection → Add MCP modal; System connector card is inert this batch.
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [addMcpOpen, setAddMcpOpen] = useState(false);
  const [addConnectorOpen, setAddConnectorOpen] = useState(false);
  const [addConnectorInitialStep, setAddConnectorInitialStep] = useState<
    'profile' | 'add-api' | 'review' | 'authentication'
  >('profile');
  const [addConnectorDismissMode, setAddConnectorDismissMode] = useState<'guard' | 'preserve'>(
    'guard'
  );

  const openOnboardingCreateModal = useCallback(() => {
    if (!showAddConnectorToolbar) return;
    setAddConnectorDismissMode('preserve');
    setTypeModalOpen(true);
  }, [showAddConnectorToolbar]);

  const openConnectorModalStep = useCallback(
    (step: 'profile' | 'add-api' | 'review' | 'authentication') => {
      setAddConnectorInitialStep(step);
      setAddConnectorDismissMode('preserve');
      setAddConnectorOpen(true);
    },
    []
  );

  const handleSelectMcp = () => {
    setTypeModalOpen(false);
    setAddMcpOpen(true);
  };

  const handleSelectSystem = () => {
    setTypeModalOpen(false);
    setAddConnectorInitialStep('profile');
    setAddConnectorDismissMode('guard');
    setAddConnectorOpen(true);
  };

  const handleMcpCreated = (mcpId: string) => {
    setAddMcpOpen(false);
    void pushWithMinimumDelay(router, buildWorkspaceAwarePath(`/${locale}/mcp/${mcpId}`), {
      skipUnsavedCheck: true,
    });
  };

  if (!isHomeStateResolved) {
    return <PageLoadingState className="min-h-[400px] bg-white" />;
  }

  if (!canViewConnectorHome) {
    return <AccessDeniedModal isOpen onClose={() => undefined} />;
  }

  return (
    <div className="flex flex-1 flex-col items-center bg-white">
      {/* Connector Banner */}
      <div className="w-full">
        <ConnectorBanner />
      </div>

      {/* Getting Started Guide - Only show for editors */}
      {showGuidance && (
        <div className="w-full">
          <GettingStartedSection
            onboardingProgress={onboardingProgress}
            flowState={flowState}
            unfinishedConnector={unfinishedConnector}
            hasLocalMcpIntegration={hasLocalMcpIntegration}
            onOpenCreateModal={openOnboardingCreateModal}
            onOpenConnectorModalStep={openConnectorModalStep}
          />
        </div>
      )}

      {showSystemList && (
        <ConnectorManagementPage
          initialTab="systems"
          showBanner={false}
          renderDialogs={false}
          onAddSystem={() => {
            setAddConnectorInitialStep('profile');
            setAddConnectorDismissMode('guard');
            setAddConnectorOpen(true);
          }}
          onAddMcp={() => setAddMcpOpen(true)}
        />
      )}

      {/* Add Connector — type selection (System | MCP), design 7952-138394.
          The System connector card is intentionally inert this batch; the MCP
          card hands off to the Add MCP connection modal below. */}
      {(canCreateSystem || canCreateMcp) && (
        <ChooseConnectionTypeModal
          open={typeModalOpen}
          onClose={() => setTypeModalOpen(false)}
          onSelectMcp={handleSelectMcp}
          onSelectSystem={handleSelectSystem}
          allowSystem={canCreateSystem}
          allowMcp={canCreateMcp}
        />
      )}
      {canCreateSystem && (
        <AddConnectorModal
          open={addConnectorOpen}
          onOpenChange={setAddConnectorOpen}
          initialStep={addConnectorInitialStep}
          dismissMode={addConnectorDismissMode}
        />
      )}
      {canCreateMcp && (
        <AddMcpModal
          open={addMcpOpen}
          onClose={() => setAddMcpOpen(false)}
          onCreated={handleMcpCreated}
        />
      )}
      <ConnectorCreatedModal
        connector={createdConnector}
        onClose={() => setCreatedConnector(null)}
      />
    </div>
  );
}
